import { useState, useEffect, useCallback } from 'react';
import { C, SCHEDULER_BACKEND } from '../constants';
import PlatformMockup from './PlatformMockup';
import YoutubeQueue from './YoutubeQueue';
import QuarantinePanel from './QuarantinePanel';
import NeedsApprovalPanel from './NeedsApprovalPanel';
import LoadErrorBanner from './LoadErrorBanner';

const API_URL = process.env.REACT_APP_MEDIA_STUDIO_API_URL || 'https://strategic-honesty-media-studio-production.up.railway.app';
const API_KEY = process.env.REACT_APP_API_KEY;

// Real intended cadence for Postiz platforms (Bluesky/Mastodon) is 2
// posts/week, not "however many happen to be queued" — a click NEVER
// schedules more than this many, no matter how large the backlog is. Also
// keeps every click well under Postiz's rate limit, which a 27-item burst
// blew straight through.
const POSTIZ_MAX_PER_CLICK = 2;

// Everything approved but not yet actually published, in one place — the gap
// this fills: Review & Publish's "Approve" flips a queue item to status
// 'published' the moment it's queued for Buffer/CSV export or (for YouTube)
// marked approved in the asset store, so it immediately vanishes from that
// screen even though nothing has really gone out yet. This reads the same
// data (buffer_queue for everything except YouTube, the asset store's
// approved-for-YouTube flag for YouTube) with the same rich preview.
export default function AwaitingPublish({ onCountChange }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // { status } when the queue could not be loaded
  const [openPlatforms, setOpenPlatforms] = useState({});
  const [channelMap, setChannelMap] = useState({}); // service -> channelId, only for channels actually connected in Buffer
  const [postizMap, setPostizMap] = useState({}); // identifier -> integrationId, only for channels actually connected in Postiz
  const [posting, setPosting] = useState({}); // platform -> bool
  const [results, setResults] = useState({}); // post.id -> { state, msg }
  const [youtubeCount, setYoutubeCount] = useState(0);

  // Sidebar badge total — "how many things still need me to click post,"
  // not counting Quarantine (that's a separate "needs a decision" bucket,
  // already shown with its own red count inside this tab).
  useEffect(() => {
    if (onCountChange) onCountChange(posts.length + youtubeCount);
  }, [posts.length, youtubeCount, onCountChange]);

  const fetchBufferQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/buffer-queue`, { headers: { 'x-api-key': API_KEY } });
      if (!res.ok) {
        setLoadError({ status: res.status });
        setPosts([]);
        return;
      }
      setLoadError(null);
      const data = await res.json();
      setPosts(data.success ? (data.posts || []) : []);
    } catch {
      setLoadError({ status: 'network' });
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBufferQueue(); }, [fetchBufferQueue]);

  useEffect(() => {
    fetch(`${SCHEDULER_BACKEND}/buffer/channels`)
      .then(r => r.json())
      .then(d => {
        const map = {};
        (d.channels || []).forEach(c => { map[c.service] = c.id; });
        setChannelMap(map);
      })
      .catch(() => setChannelMap({}));

    fetch(`${SCHEDULER_BACKEND}/posts/postiz/integrations`)
      .then(r => r.json())
      .then(list => {
        const map = {};
        (Array.isArray(list) ? list : []).forEach(i => { map[i.identifier] = i.id; });
        setPostizMap(map);
      })
      .catch(() => setPostizMap({}));
  }, []);

  // "facebook reels" / "instagram reels" are the SAME channel connection as
  // regular Facebook/Instagram — just a different post type, not a separate
  // Buffer channel to look up.
  function toBufferService(platform) {
    const p = (platform || '').toLowerCase();
    if (p === 'facebook reels') return { service: 'facebook', postType: 'reel' };
    if (p === 'instagram reels') return { service: 'instagram', postType: 'reel' };
    return { service: p, postType: 'post' };
  }

  // Postiz's own integration `identifier` values don't always match the
  // queue's platform names 1:1 (Instagram is "instagram-standalone" there,
  // X is "x" not "twitter") — this maps queue platform -> Postiz identifier,
  // same idea as toBufferService but for the Postiz side.
  function toPostizIdentifier(platform) {
    const p = (platform || '').toLowerCase();
    if (p === 'instagram' || p === 'instagram reels') return 'instagram-standalone';
    if (p === 'twitter' || p === 'x') return 'x';
    return p;
  }

  // Which provider (if any) actually has this platform connected — Buffer
  // and Postiz are two separate publishing paths, so a platform section
  // only gets a "Post via X" button when one of them really has it.
  function providerFor(platform) {
    const { service } = toBufferService(platform);
    if (channelMap[service]) return { kind: 'buffer' };
    if (postizMap[toPostizIdentifier(platform)]) return { kind: 'postiz' };
    return null;
  }

  async function postPlatformViaBuffer(platform, items) {
    const { service, postType } = toBufferService(platform);
    const channelId = channelMap[service];
    const nextResults = {};

    for (const post of items) {
      const mediaUrl = post.video_url || post.image_url || post.pinterest_url || null;
      try {
        const res = await fetch(`${SCHEDULER_BACKEND}/buffer/post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            channelId,
            text: post.content,
            mediaUrl,
            mediaType: mediaUrl ? (post.video_url ? 'video' : 'image') : undefined,
            service,
            postType,
            bufferQueueId: post.id,
            ideaId: post.idea_id,
            title: post.title,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Buffer post failed');

        await fetch(`${API_URL}/api/buffer-queue/${post.id}`, {
          method: 'DELETE',
          headers: { 'x-api-key': API_KEY },
        });
        nextResults[post.id] = { state: 'ok', msg: 'Posted to Buffer' };
      } catch (e) {
        nextResults[post.id] = { state: 'err', msg: e.message };
      }
    }
    return nextResults;
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Postiz has no "add to queue, decide the time for me" mode like Buffer —
  // every post needs an explicit future date — so the (at most 2) items are
  // spread a few days apart, matching a twice-a-week cadence.
  // Postiz's own API is aggressively rate-limited (429s even a few seconds
  // apart), so each request waits, and retries with backoff on 429.
  async function postPlatformViaPostiz(platform, allItems) {
    const items = allItems.slice(0, POSTIZ_MAX_PER_CLICK);
    const integrationId = postizMap[toPostizIdentifier(platform)];
    const nextResults = {};
    const base = new Date();
    base.setUTCDate(base.getUTCDate() + 2);
    base.setUTCHours(15, 0, 0, 0); // 3pm UTC, arbitrary but consistent
    const gapDays = 3; // ~twice a week

    const postizPlatformType = toPostizIdentifier(platform);
    // Postiz validates a platform-specific `settings` shape server-side —
    // Instagram only accepts post_type 'post' or 'story' (no distinct
    // "reel" value; a vertical/short video still displays as a Reel on
    // Instagram's own feed based on the video itself, same as YouTube
    // Shorts), and X requires an explicit reply-audience setting.
    const settingsFor = {
      'instagram-standalone': { post_type: 'post' },
      x: { who_can_reply_post: 'everyone' },
    };
    const settings = settingsFor[postizPlatformType] || {};

    for (let i = 0; i < items.length; i++) {
      const post = items[i];
      const mediaUrl = post.video_url || post.image_url || post.pinterest_url || null;
      const date = new Date(base.getTime() + i * gapDays * 24 * 60 * 60 * 1000).toISOString();
      try {
        let res, data;
        for (let attempt = 0; attempt < 6; attempt++) {
          res = await fetch(`${SCHEDULER_BACKEND}/posts/postiz/publish`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              integrationId,
              content: post.content,
              images: mediaUrl ? [{ url: mediaUrl }] : [],
              platformType: postizPlatformType,
              settings,
              date,
              bufferQueueId: post.id,
              ideaId: post.idea_id,
              title: post.title,
            }),
          });
          data = await res.json();
          if (res.status !== 429) break;
          await sleep(15000);
        }
        if (!res.ok || !data.success) throw new Error(data.error || 'Postiz post failed');

        await fetch(`${API_URL}/api/buffer-queue/${post.id}`, {
          method: 'DELETE',
          headers: { 'x-api-key': API_KEY },
        });
        nextResults[post.id] = { state: 'ok', msg: `Scheduled via Postiz for ${new Date(date).toLocaleString()}` };
      } catch (e) {
        nextResults[post.id] = { state: 'err', msg: e.message };
      }
      await sleep(4000); // stay under Postiz's rate limit between items
    }
    return nextResults;
  }

  async function deletePost(post) {
    if (!window.confirm('Delete this item permanently? This cannot be undone.')) return;
    try {
      await fetch(`${API_URL}/api/buffer-queue/${post.id}`, {
        method: 'DELETE',
        headers: { 'x-api-key': API_KEY },
      });
      setPosts(prev => prev.filter(p => p.id !== post.id));
    } catch (e) {
      setResults(prev => ({ ...prev, [post.id]: { state: 'err', msg: `Delete failed: ${e.message}` } }));
    }
  }

  async function postPlatform(platform, items) {
    setPosting(prev => ({ ...prev, [platform]: true }));
    const provider = providerFor(platform);
    const nextResults = provider?.kind === 'postiz'
      ? await postPlatformViaPostiz(platform, items)
      : await postPlatformViaBuffer(platform, items);
    setResults(prev => ({ ...prev, ...nextResults }));
    setPosting(prev => ({ ...prev, [platform]: false }));
    fetchBufferQueue();
  }

  const byPlatform = posts.reduce((acc, p) => {
    (acc[p.platform] = acc[p.platform] || []).push(p);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>🚦 Waiting to Publish</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Posts you have already approved that have not gone out yet. Nothing here means nothing is waiting.</div>
        </div>
        <button onClick={fetchBufferQueue} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, border: `1px solid ${C.border}`, borderRadius: 7, background: '#fff', cursor: 'pointer', color: C.muted }}>↻ Refresh</button>
      </div>

      <NeedsApprovalPanel />
      {loadError && <LoadErrorBanner status={loadError.status} />}
      <YoutubeQueue onCountChange={setYoutubeCount} />
      <QuarantinePanel />

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 3 }}>📥 Other platforms — approved, not yet exported</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Approved in Review Finished Assets ("Approve & Publish") — pending Buffer CSV export.</div>

        {loading ? (
          <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>
        ) : posts.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Nothing awaiting export.</div>
        ) : (
          Object.entries(byPlatform).map(([platform, items]) => {
            const open = openPlatforms[platform] === true;
            const provider = providerFor(platform);
            // Surfaced right at the button, not just buried per-item inside
            // the expanded view — a failed click (e.g. Buffer's "10 scheduled
            // posts" cap) previously only showed an error if you happened to
            // expand the section, so it looked like nothing happened at all.
            const errorResults = items.map(p => results[p.id]).filter(r => r && r.state === 'err');
            const summaryError = errorResults[0]?.msg;
            return (
              <div key={platform} style={{ marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f8fafc' }}>
                  <div onClick={() => setOpenPlatforms(prev => ({ ...prev, [platform]: !open }))} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text, textTransform: 'capitalize' }}>{platform}</span>
                    <span style={{ fontSize: 11, color: '#fff', background: C.navy, borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>{items.length}</span>
                  </div>
                  {provider && (
                    <button
                      onClick={() => postPlatform(platform, items)}
                      disabled={posting[platform]}
                      style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, border: 'none', borderRadius: 7, background: posting[platform] ? '#e0e0e0' : (provider.kind === 'postiz' ? '#8B5CF6' : '#635BFF'), color: posting[platform] ? '#999' : '#fff', cursor: posting[platform] ? 'not-allowed' : 'pointer' }}
                    >
                      {posting[platform] ? 'Posting…' : provider.kind === 'postiz' ? `🚀 Post next ${Math.min(POSTIZ_MAX_PER_CLICK, items.length)} via Postiz` : '🚀 Post via Buffer'}
                    </button>
                  )}
                  <span style={{ fontSize: 12, color: C.muted, cursor: 'pointer' }} onClick={() => setOpenPlatforms(prev => ({ ...prev, [platform]: !open }))}>{open ? '▲' : '▼'}</span>
                </div>
                {summaryError && (
                  <div style={{ padding: '8px 14px', background: '#fef2f2', borderTop: `1px solid #fecaca`, fontSize: 12, color: '#dc2626' }}>
                    ✗ {summaryError}{errorResults.length > 1 ? ` (and ${errorResults.length - 1} more)` : ''} — will retry automatically once there's room; nothing was lost.
                  </div>
                )}
                {open && (
                  <div style={{ padding: 14 }}>
                    {items.map(post => {
                      const mediaUrl = post.video_url || post.image_url || post.pinterest_url || null;
                      const result = results[post.id];
                      return (
                        <div key={post.id}>
                          <PlatformMockup
                            platform={post.platform}
                            caption={post.content}
                            mediaUrl={mediaUrl}
                            isVideoUrl={!!post.video_url}
                          />
                          <div style={{ display: 'flex', justifyContent: 'center', marginTop: -8, marginBottom: 12 }}>
                            <button
                              onClick={() => deletePost(post)}
                              style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer' }}
                            >
                              🗑 Delete
                            </button>
                          </div>
                          {result && (
                            <div style={{ marginTop: -8, marginBottom: 12, fontSize: 12, color: result.state === 'ok' ? '#16a34a' : '#dc2626' }}>
                              {result.state === 'ok' ? '✓' : '✗'} {result.msg}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
